import { Button, CheckboxGroup, Fields } from '@ets-global/b2c-website-ui';
import { FieldArray, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Trans, useTranslation } from 'react-i18next';

import { CONTACT_FORM_TEST_USAGES } from '../../../constants';
import { NB_CANDIDATE_OPTIONS } from '../../../graphql/queries/optionsSalesForce';
import { useDataLayer } from '../../../hooks';
import useQuery from '../../../hooks/useQuery';
import useYup from '../../../hooks/useYup';

const Step3 = ({ initialValues = {}, onSubmit = () => {}, onCancel = () => {} }) => {
  const { t } = useTranslation();
  const { Yup } = useYup();
  const { pushEventLoadedB2BContactFunnel } = useDataLayer();
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    pushEventLoadedB2BContactFunnel({
      stepName: 'product and services usage',
      stepNumber: 3,
    });
  }, []);

  const B2BContactFormStep3Schema = Yup.object().shape({
    testUsages: Yup.array(),
    products: Yup.array(),
    conditions: Yup.boolean().oneOf([true], t('common.form.validation.required')),
    rgpd: Yup.boolean().oneOf([true], t('common.form.validation.required')),
    productNbCandidate: Yup.string(),
    message: Yup.string().required(),
  });

  const { data: nbCandidateData, loading: nbCandidateLoading } = useQuery(NB_CANDIDATE_OPTIONS, {});
  let nbCandidate = [];
  if (!nbCandidateLoading) {
    nbCandidate = nbCandidateData.salesforce.nbCandidateOptions.map(({ label, value }) => ({ label, value }));
    nbCandidate.unshift({ label: '', value: '' });
  }

  const handleSubmit = async (data, actions) => {
    if (!executeRecaptcha) {
      return;
    }

    const recaptcha = await executeRecaptcha('b2c_website_contact_form');

    [CONTACT_FORM_TEST_USAGES.RESELLER, CONTACT_FORM_TEST_USAGES.ORGANISATION].forEach(
      (usage) => (data[usage] = data.testUsages.includes(usage))
    );
    data.productSelect = data.products;
    data.recaptcha = recaptcha;

    onSubmit(data, actions);
  };

  return (
    <Formik
      initialValues={{
        message: '',
        products: [],
        testUsages: [],
        ...initialValues,
        conditions: false,
        rgpd: false,
      }}
      validationSchema={B2BContactFormStep3Schema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form className="contact-form">
          <div className="contact-form__main-inner">
            <div className="contact-form__main-column">
              <div className="contact-form__fieldset">
                <h4 className="contact-form__fieldset-label">
                  <Trans>contact.form.test-usage</Trans>
                </h4>
                <div className="contact-form__test-usages">
                  <FieldArray
                    name={'testUsages'}
                    render={(arrayHelpers) => (
                      <CheckboxGroup
                        arrayHelpers={arrayHelpers}
                        name={'testUsages'}
                        options={[
                          {
                            value: CONTACT_FORM_TEST_USAGES.RESELLER,
                            label: t('contact.form.label.organisation.product-reseller'),
                          },
                          {
                            value: CONTACT_FORM_TEST_USAGES.ORGANISATION,
                            label: t('contact.form.label.organisation.product-organisation'),
                          },
                        ]}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="contact-form__fieldset">
                <h4 className="contact-form__fieldset-label">
                  <Trans>contact.form.test-interested-in</Trans>
                </h4>
                <div className="contact-form__products">
                  <FieldArray name={'products'} component={Fields.ProductsFields} />
                </div>
              </div>
            </div>
            <div className="contact-form__main-column">
              <div className="contact-form__fieldset">
                <Fields.FormikFastField
                  id="productNbCandidate"
                  name="productNbCandidate"
                  component={Fields.SelectField}
                  label={t('contact.form.label.organisation.candidates')}
                  autoComplete={'contact productNbCandidate'}
                  options={nbCandidate}
                />
                <Fields.FormikFastField
                  id="message"
                  name="message"
                  component={Fields.TextareaField}
                  label={t('contact.form.label.organisation.message.title')}
                  instructions={t('contact.form.label.organisation.message.instructions')}
                  autoComplete={'contact message'}
                  maxLength={240}
                  onChange={(e) => {
                    e.target.style.height = '30px';
                    e.target.style.height = e.target.scrollHeight + 5 + 'px';
                  }}
                  required={true}
                />
              </div>
            </div>
          </div>
          <div className="contact-form__checkboxes">
            <Fields.FormikFastField
              id="rgpd"
              name="rgpd"
              component={Fields.CheckboxField}
              label={t('contact.form.label.organisation.rgpd')}
              required={true}
            />

            <Fields.FormikFastField
              id="conditions"
              name="conditions"
              component={Fields.CheckboxField}
              label={t('contact.form.label.organisation.conditions')}
              required={true}
            />
          </div>
          <div className="contact-form__buttons">
            <Button type="submit" disabled={isSubmitting} aria-label={t('contact.form.button.send')}>
              <Trans>contact.form.button.send</Trans>
            </Button>
            <Button
              type="button"
              className="button--white"
              onClick={onCancel}
              aria-label={t('contact.form.button.back')}
            >
              <Trans>contact.form.button.back</Trans>
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

Step3.propTypes = {
  initialValues: PropTypes.shape({
    productNbCandidate: PropTypes.string,
    message: PropTypes.string,
    rgpd: PropTypes.bool,
    conditions: PropTypes.bool,
    testUsages: PropTypes.array,
    products: PropTypes.array,
  }),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default Step3;
